export type BookmarkView = {
  a: number
  d: number
  fovy: number
  roll: number
}

export type Bookmark = {
  name: string
  view: BookmarkView
  isFolder: false
}

export type BookmarkFolder = {
  name: string
  children: BookmarkEntry[]
  isFolder: true
}

export type BookmarkEntry = Bookmark | BookmarkFolder

export type BookmarkWalkConfig = {
  duration: number
  easing: string
  keepFovy: boolean
  wait: number
  loop: boolean
}

export type BookmarkModuleState = {
  root: BookmarkFolder
  config: {
    walk: BookmarkWalkConfig
  }
}

export type FolderPath = number[]

export const defaultBookmarkModuleState = (): BookmarkModuleState => ({
  root: newBookmarkFolder('$root'),
  config: {
    walk: {
      duration: 4000,
      easing: 'slowStartStop4',
      keepFovy: false,
      wait: 1000,
      loop: true,
    },
  },
})

export function newBookmark(name: string, view: BookmarkView): Bookmark {
  return { name, view, isFolder: false }
}

export function newBookmarkFolder(name: string, children: BookmarkEntry[] = []): BookmarkFolder {
  return { name, children, isFolder: true }
}

export function cloneBookmarkState(state: BookmarkModuleState): BookmarkModuleState {
  return JSON.parse(JSON.stringify(state)) as BookmarkModuleState
}

export function isBookmarkFolder(value: unknown): value is BookmarkFolder {
  if (!value || typeof value !== 'object') {
    return false
  }
  const folder = value as Partial<BookmarkFolder>
  return folder.isFolder === true
    && typeof folder.name === 'string'
    && Array.isArray(folder.children)
    && folder.children.every(isBookmarkEntry)
}

function isBookmarkEntry(value: unknown): value is BookmarkEntry {
  if (!value || typeof value !== 'object') {
    return false
  }
  const entry = value as Partial<BookmarkEntry>
  if (entry.isFolder === true) {
    return isBookmarkFolder(value)
  }
  if (entry.isFolder !== false || typeof entry.name !== 'string') {
    return false
  }
  const view = (entry as Partial<Bookmark>).view
  return !!view
    && Number.isFinite(view.a)
    && Number.isFinite(view.d)
    && Number.isFinite(view.fovy)
    && Number.isFinite(view.roll)
}

export function normalizeBookmarkModuleState(value: unknown): BookmarkModuleState {
  const defaults = defaultBookmarkModuleState()
  if (!value || typeof value !== 'object') {
    return defaults
  }

  const moduleState = value as {
    root?: unknown
    config?: {
      walk?: Partial<BookmarkWalkConfig>
    }
  }
  const root = isBookmarkFolder(moduleState.root) ? moduleState.root : defaults.root
  const walk = moduleState.config?.walk

  return {
    root,
    config: {
      walk: {
        duration: finiteNumberOr(walk?.duration, defaults.config.walk.duration),
        easing: typeof walk?.easing === 'string' ? walk.easing : defaults.config.walk.easing,
        keepFovy: typeof walk?.keepFovy === 'boolean' ? walk.keepFovy : defaults.config.walk.keepFovy,
        wait: finiteNumberOr(walk?.wait, defaults.config.walk.wait),
        loop: typeof walk?.loop === 'boolean' ? walk.loop : defaults.config.walk.loop,
      },
    },
  }
}

function finiteNumberOr(value: unknown, fallback: number) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

export function getFolder(root: BookmarkFolder, path: FolderPath): BookmarkFolder {
  let folder = root
  for (const index of path) {
    const child = folder.children[index]
    if (!child?.isFolder) {
      throw new Error(`Invalid bookmark folder path: ${encodeFolderPath(path)}`)
    }
    folder = child
  }
  return folder
}

export function tryGetFolder(root: BookmarkFolder, path: FolderPath): BookmarkFolder | undefined {
  try {
    return getFolder(root, path)
  } catch {
    return undefined
  }
}


export function appendBookmarkFolder(root: BookmarkFolder, path: FolderPath, name: string) {
  const folder = getFolder(root, path)
  const childPath = [...path, folder.children.length]
  folder.children.push(newBookmarkFolder(name))
  return childPath
}

export function renameBookmarkEntry(root: BookmarkFolder, folderPath: FolderPath, index: number, name: string) {
  const entry = getFolder(root, folderPath).children[index]
  if (!entry) throw new Error('Bookmark not found')
  entry.name = name
}

export function removeBookmarkEntry(root: BookmarkFolder, folderPath: FolderPath, index: number) {
  const children = getFolder(root, folderPath).children
  if (index < 0 || index >= children.length) throw new Error('Bookmark not found')
  children.splice(index, 1)
}

export function reorderBookmarkEntry(root: BookmarkFolder, folderPath: FolderPath, index: number, delta: -1 | 1) {
  const children = getFolder(root, folderPath).children
  const nextIndex = index + delta
  if (index < 0 || nextIndex < 0 || index >= children.length || nextIndex >= children.length) return
  ;[children[index], children[nextIndex]] = [children[nextIndex], children[index]]
}

export function moveBookmarkEntry(root: BookmarkFolder, sourcePath: FolderPath, index: number, destinationPath: FolderPath) {
  const children = getFolder(root, sourcePath).children
  const entry = children[index]
  if (!entry) throw new Error('Bookmark not found')
  if (entry.isFolder && isPathPrefix([...sourcePath, index], destinationPath)) {
    throw new Error('Cannot move a folder inside itself')
  }
  if (encodeFolderPath(sourcePath) === encodeFolderPath(destinationPath)) return
  const destination = getFolder(root, destinationPath)
  children.splice(index, 1)
  destination.children.push(entry)
}

export type FolderOption = {
  path: FolderPath
  label: string
}

export function folderOptions(
  root: BookmarkFolder,
  excludeEntryPath?: FolderPath,
): FolderOption[] {
  const result: FolderOption[] = []

  const visit = (folder: BookmarkFolder, path: FolderPath, label: string) => {
    result.push({ path, label })
    folder.children.forEach((entry, index) => {
      if (!entry.isFolder) {
        return
      }
      const childPath = [...path, index]
      if (excludeEntryPath && isPathPrefix(excludeEntryPath, childPath)) {
        return
      }
      visit(entry, childPath, `${label}${entry.name}/`)
    })
  }

  visit(root, [], '/')
  return result
}

function isPathPrefix(prefix: FolderPath, path: FolderPath) {
  return prefix.length <= path.length && prefix.every((part, index) => path[index] === part)
}

export function encodeFolderPath(path: FolderPath) {
  return path.join('.')
}

export function decodeFolderPath(value: string): FolderPath {
  if (!value) {
    return []
  }
  return value.split('.').map(Number)
}

export function childBookmarks(root: BookmarkFolder) {
  const bookmarks: Bookmark[] = []
  const visit = (entry: BookmarkEntry) => {
    if (entry.isFolder) {
      entry.children.forEach(visit)
    } else {
      bookmarks.push(entry)
    }
  }
  visit(root)
  return bookmarks
}
